/**
 * 岩点颜色聚类 → 颜色线路 —— 逐行对拍 climb-core/src/cluster.rs
 * Lab 色度平面 (a*,b*) 平均链接凝聚聚类（tol_ab=14）+ 黑/白/灰特判成组。
 */
import { abAngle, colorNameAb, hueDist, labToRgb } from "./color";
import { HoldKind, type Cluster, type Hold } from "./types";

function abDist(a: readonly [number, number], b: readonly [number, number]): number {
  return Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2);
}

/** 低色度簇展示色卡提亮：色度 <28 按比例放大（仅显示用途）。对拍 cluster.rs:201 */
export function displayRgb(ab: readonly [number, number]): [number, number, number] {
  const c = Math.sqrt(ab[0] * ab[0] + ab[1] * ab[1]);
  if (c < 1) return [150, 150, 155];
  const scale = c < 28 ? 28 / c : 1;
  return labToRgb(65, ab[0] * scale, ab[1] * scale);
}

const KIND_RGB: Record<Exclude<HoldKind, HoldKind.Colored>, [number, number, number]> = {
  [HoldKind.Black]: [46, 46, 52],
  [HoldKind.White]: [235, 235, 238],
  [HoldKind.Gray]: [150, 150, 155],
};

/** Lab 色度平面平均链接凝聚聚类 + 黑/白/灰特判。对拍 build_clusters */
export function buildClusters(holds: Hold[], tolAb: number): Cluster[] {
  interface Agg {
    holds: number[];
    ab: [number, number];
    w: number;
  }
  const aggs: Agg[] = [];
  for (let i = 0; i < holds.length; i++) {
    const hd = holds[i];
    if (hd.kind !== HoldKind.Colored) continue;
    aggs.push({ holds: [i], ab: [hd.ab[0], hd.ab[1]], w: Math.max(hd.area, 1) });
  }

  // 平均链接凝聚：反复合并质心距离最近的簇对，直至最近距离 >= tol_ab
  for (;;) {
    if (aggs.length <= 1) break;
    let bestI = -1;
    let bestJ = -1;
    let bestD = Infinity;
    for (let i = 0; i < aggs.length; i++) {
      for (let j = i + 1; j < aggs.length; j++) {
        const d = abDist(aggs[i].ab, aggs[j].ab);
        if (d < bestD) {
          bestI = i;
          bestJ = j;
          bestD = d;
        }
      }
    }
    if (bestI < 0 || bestD >= tolAb) break;
    // swap_remove(j) 语义：把末尾元素换到 j 再弹出 —— 影响后续 aggs 顺序，须复刻
    const bj = aggs[bestJ];
    const last = aggs.pop()!;
    if (bestJ < aggs.length) aggs[bestJ] = last;
    const bi = aggs[bestI];
    const tw = bi.w + bj.w;
    bi.ab = [
      (bi.ab[0] * bi.w + bj.ab[0] * bj.w) / tw,
      (bi.ab[1] * bi.w + bj.ab[1] * bj.w) / tw,
    ];
    bi.w = tw;
    bi.holds.push(...bj.holds);
  }

  const clusters: Cluster[] = aggs.map((ag) => {
    const hue = abAngle(ag.ab[0], ag.ab[1]);
    const rgb = displayRgb(ag.ab);
    let area = 0;
    for (const i of ag.holds) area += holds[i].area;
    return { kind: HoldKind.Colored, hue, ab: ag.ab, rgb, holds: ag.holds, area };
  });

  // 黑 / 白 / 灰：不走色度聚类
  for (const kind of [HoldKind.Black, HoldKind.White, HoldKind.Gray] as const) {
    const ids: number[] = [];
    for (let i = 0; i < holds.length; i++) {
      if (holds[i].kind === kind) ids.push(i);
    }
    if (ids.length === 0) continue;
    let area = 0;
    for (const i of ids) area += holds[i].area;
    clusters.push({ kind, hue: -1, ab: [0, 0], rgb: KIND_RGB[kind], holds: ids, area });
  }

  // 按总面积降序
  clusters.sort((a, b) => b.area - a.area);
  for (let ci = 0; ci < clusters.length; ci++) {
    for (const hi of clusters[ci].holds) {
      holds[hi].cluster = ci;
    }
  }
  return clusters;
}

/** 给（套索补测的）新岩点分配颜色组；色度距离过远时新建组。对拍 assign_hold */
export function assignHold(
  clusters: Cluster[],
  hold: Hold,
  tolAb: number,
  holdId: number,
): number {
  if (hold.kind !== HoldKind.Colored) {
    for (let ci = 0; ci < clusters.length; ci++) {
      const c = clusters[ci];
      if (c.kind === hold.kind) {
        c.holds.push(holdId);
        c.area += hold.area;
        return ci;
      }
    }
    clusters.push({
      kind: hold.kind,
      hue: -1,
      ab: [0, 0],
      rgb: KIND_RGB[hold.kind as Exclude<HoldKind, HoldKind.Colored>],
      holds: [holdId],
      area: hold.area,
    });
    return clusters.length - 1;
  }
  let bestCi = -1;
  let bestD = Infinity;
  for (let ci = 0; ci < clusters.length; ci++) {
    const c = clusters[ci];
    if (c.kind !== HoldKind.Colored) continue;
    const d = abDist(c.ab, hold.ab);
    if (d < bestD) {
      bestCi = ci;
      bestD = d;
    }
  }
  if (bestCi >= 0 && bestD <= tolAb * 1.5) {
    const c = clusters[bestCi];
    const tw = c.area + hold.area;
    c.ab = [
      (c.ab[0] * c.area + hold.ab[0] * hold.area) / tw,
      (c.ab[1] * c.area + hold.ab[1] * hold.area) / tw,
    ];
    c.hue = abAngle(c.ab[0], c.ab[1]);
    c.rgb = displayRgb(c.ab);
    c.holds.push(holdId);
    c.area += hold.area;
    return bestCi;
  }
  clusters.push({
    kind: HoldKind.Colored,
    hue: abAngle(hold.ab[0], hold.ab[1]),
    ab: [hold.ab[0], hold.ab[1]],
    rgb: displayRgb(hold.ab),
    holds: [holdId],
    area: hold.area,
  });
  return clusters.length - 1;
}

/** 颜色组显示名（Lab 色度角命名） */
export function clusterName(c: Cluster): string {
  if (c.kind === HoldKind.Colored) {
    const [zh, en] = colorNameAb(c.ab[0], c.ab[1]);
    return `${zh} ${en}`;
  }
  if (c.kind === HoldKind.Black) return "黑 black";
  if (c.kind === HoldKind.White) return "白 white";
  return "灰 gray";
}

/** 色相匹配（CLI --hue 用） */
export function hueMatch(c: Cluster, hue: number, window: number): boolean {
  return c.kind === HoldKind.Colored && hueDist(c.hue, hue) <= window;
}

/** 同名组消歧：同名的按面积顺序追加 ①②③…（对拍 disambiguated_names） */
export function disambiguatedNames(clusters: Cluster[]): string[] {
  const base = clusters.map(clusterName);
  const totals = new Map<string, number>();
  for (const n of base) totals.set(n, (totals.get(n) ?? 0) + 1);
  const seen = new Map<string, number>();
  const CIR = ["①", "②", "③", "④", "⑤", "⑥", "⑦", "⑧", "⑨", "⑩"];
  return base.map((n) => {
    if ((totals.get(n) ?? 0) <= 1) return n;
    const k = (seen.get(n) ?? 0) + 1;
    seen.set(n, k);
    const tag = CIR[k - 1] ?? "•";
    return `${n} ${tag}`;
  });
}
